/**
 * The one place a paid-media provider is turned into a human label or an
 * authoring route.
 *
 * Both facts were previously inlined as two-way ternaries — the chooser dialog
 * held its own route map while `PaidCampaignDraftsPage` asked
 * `provider === 'google-ads' ? 'Google Ads' : 'Meta'`. That ternary was
 * correct only while exactly two providers existed: once `linkedin-ads`
 * shipped, a LinkedIn draft rendered the Meta label and its Resume button
 * navigated to the Meta authoring page, silently loading a LinkedIn payload
 * into the wrong surface. A record keyed by the provider union makes the next
 * provider a type error at every call site instead of a wrong answer at one.
 */

export type PaidMediaProvider = 'google-ads' | 'meta-ads' | 'linkedin-ads';

export const PAID_MEDIA_PROVIDER_LABELS: Record<PaidMediaProvider, string> = {
  'meta-ads': 'Meta',
  'google-ads': 'Google Ads',
  'linkedin-ads': 'LinkedIn',
};

/** The one creator route, with the provider chosen. The `/campaigns/{meta,google,linkedin}/new` aliases only redirect here. */
export const PAID_MEDIA_PROVIDER_ROUTES: Record<PaidMediaProvider, string> = {
  'meta-ads': '/campaigns/ads/new?provider=meta-ads',
  'google-ads': '/campaigns/ads/new?provider=google-ads',
  'linkedin-ads': '/campaigns/ads/new?provider=linkedin-ads',
};

/**
 * The retired per-provider addresses. Two callers still need them on purpose: a legacy Campaign draft
 * id rides in `?draft=`, which only the alias translates to the creator's `v2`, and the creator's
 * not-ready fallback hands off to the legacy wizard the alias hosts. Nothing else may emit them.
 */
export const PAID_MEDIA_LEGACY_AUTHORING_ROUTES: Record<PaidMediaProvider, string> = {
  'meta-ads': '/campaigns/meta/new',
  'google-ads': '/campaigns/google/new',
  'linkedin-ads': '/campaigns/linkedin/new',
};

/**
 * L7 (2026-09-23) — the Ads rail provider cards now open a conversation with
 * `paid-campaign-guide`, provider preselected, instead of navigating straight
 * to the manual wizard (see `AdsAgentRail.tsx`'s doc comment and
 * `CreativeChatSuggestions.tsx`'s `chooseAdProvider`). One launch prompt per
 * provider, written in code rather than by the model, so the guide's first
 * structured question always has the provider already settled.
 */
export const PAID_MEDIA_GUIDE_LAUNCH_PROMPTS: Record<PaidMediaProvider, string> = {
  'meta-ads': 'Help me set up a Meta Ads campaign.',
  'google-ads': 'Help me set up a Google Ads campaign.',
  'linkedin-ads': 'Help me set up a LinkedIn Ads campaign.',
};

/**
 * A persisted draft's provider arrives as a plain string from the database, so
 * an unrecognised value is possible in a way the union type does not admit.
 * Name it honestly rather than defaulting to whichever provider happens to be
 * first — a mislabelled draft is the bug this module exists to prevent.
 */
export function paidMediaProviderLabel(provider: string): string {
  return PAID_MEDIA_PROVIDER_LABELS[provider as PaidMediaProvider] ?? 'Unknown provider';
}

/** Null when the provider has no authoring surface to resume into. */
export function paidMediaAuthoringRoute(provider: string): string | null {
  return PAID_MEDIA_LEGACY_AUTHORING_ROUTES[provider as PaidMediaProvider] ?? null;
}
